import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { FiBell, FiCheck, FiFileText, FiMessageCircle, FiRefreshCw, FiX } from "react-icons/fi";
import { GiScales } from "react-icons/gi";
import { AppAPIService } from "../../services/AppAPIService";
import "./NotificationCenter.css";

const EMPTY = { unreadCount: 0, items: [], history: [] };
const formatDate = value => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
const iconFor = type => type === "document" ? FiFileText : type === "message" ? FiMessageCircle : GiScales;

export default function NotificationCenter({ pathname }) {
  const api = AppAPIService.getInstance();
  const history = useHistory();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const response = await api.makeHttpRequest({ url: "/notifications", method: "GET" });
      setData({ ...EMPTY, ...(response || {}) });
      setError("");
    } catch (requestError) {
      setError("Não foi possível carregar as notificações.");
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => load({ quiet: true }), 60000);
    const refreshVisible = () => document.visibilityState === "visible" && load({ quiet: true });
    document.addEventListener("visibilitychange", refreshVisible);
    return () => { window.clearInterval(timer); document.removeEventListener("visibilitychange", refreshVisible); };
  }, [load]);

  useEffect(() => { setOpen(false); load({ quiet: true }); }, [pathname, load]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = event => !rootRef.current?.contains(event.target) && setOpen(false);
    const closeEscape = event => event.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", closeOutside);
    document.addEventListener("keydown", closeEscape);
    return () => { document.removeEventListener("mousedown", closeOutside); document.removeEventListener("keydown", closeEscape); };
  }, [open]);

  const mutate = async (key, operation) => {
    await api.makeHttpRequest({ url: key ? `/notifications/${encodeURIComponent(key)}/${operation}` : "/notifications/read-all", method: "PATCH" });
    await load({ quiet: true });
  };
  const openItem = async item => {
    try { await mutate(item.notificationKey, "read"); } catch (requestError) { /* navigation remains available */ }
    setOpen(false);
    history.push(item.actionUrl);
  };
  const items = tab === "pending" ? data.items || [] : data.history || [];
  const unreadLabel = data.unreadCount > 99 ? "99+" : data.unreadCount;

  return <div className="xv-notifications" ref={rootRef}>
    <button type="button" className="xv-icon-button xv-notifications__trigger" aria-label={`Notificações${data.unreadCount ? `: ${data.unreadCount} não lidas` : ""}`} aria-expanded={open} aria-controls="xv-notification-panel" onClick={() => setOpen(current => !current)}>
      <FiBell />{data.unreadCount > 0 && <span className="xv-notifications__badge">{unreadLabel}</span>}
    </button>
    {open && <section id="xv-notification-panel" className="xv-notifications__panel" aria-label="Central de notificações">
      <header className="xv-notifications__header"><div><strong>Notificações</strong><small>Ações que precisam da sua atenção</small></div><button type="button" className="xv-notifications__close" aria-label="Fechar notificações" onClick={() => setOpen(false)}><FiX /></button></header>
      <div className="xv-notifications__tabs" role="tablist"><button type="button" role="tab" aria-selected={tab === "pending"} onClick={() => setTab("pending")}>Pendências <span>{data.items?.length || 0}</span></button><button type="button" role="tab" aria-selected={tab === "history"} onClick={() => setTab("history")}>Histórico</button></div>
      {tab === "pending" && data.unreadCount > 0 && <button type="button" className="xv-notifications__read-all" onClick={() => mutate(null, "read-all").catch(() => setError("Não foi possível atualizar as notificações."))}><FiCheck /> Marcar todas como lidas</button>}
      <div className="xv-notifications__list" aria-live="polite">
        {loading && <div className="xv-notifications__state"><FiRefreshCw className="is-spinning" /> Carregando notificações...</div>}
        {!loading && error && <div className="xv-notifications__state"><span>{error}</span><button type="button" onClick={() => load()}>Tentar novamente</button></div>}
        {!loading && !error && items.length === 0 && <div className="xv-notifications__state"><FiCheck /><strong>{tab === "pending" ? "Tudo em dia" : "Nenhum item no histórico"}</strong><span>{tab === "pending" ? "Você não possui nenhuma pendência neste momento." : "As notificações concluídas aparecerão aqui."}</span></div>}
        {!loading && !error && items.map(item => {
          const Icon = iconFor(item.type);
          return <article key={item.notificationKey} className={`xv-notification xv-notification--${item.priority} ${item.readAt ? "is-read" : "is-unread"}`}><span className="xv-notification__icon"><Icon /></span><div className="xv-notification__body"><strong>{item.title}</strong><p>{item.message}</p><time>{formatDate(item.sourceAt || item.createdAt)}</time><div className="xv-notification__actions">{item.actionUrl && <button type="button" onClick={() => openItem(item)}>{item.actionLabel || "Ver detalhes"}</button>}{tab === "pending" && <button type="button" className="is-secondary" onClick={() => mutate(item.notificationKey, "dismiss").catch(() => setError("Não foi possível dispensar a notificação."))}>Dispensar</button>}</div></div></article>;
        })}
      </div>
    </section>}
  </div>;
}
